/**
 * The ScreenContext registry (replaces useAssistantContext).
 *
 * Views publish frames with `useScreenFrame(() => ({ ... }))`: the getter is reactive, so selection
 * changes and composer edits flow through automatically, and the frame is removed on unmount.
 * `snapshotScreen()` composes every live frame into one ScreenContext for the next agent message.
 */
import { getCurrentInstance, onBeforeUnmount, ref, watch, type Ref } from 'vue'
import { LIMITS, type ScreenContext, type ScreenEntity, type ScreenFrame } from './types'

interface Registered {
    id: number
    frame: ScreenFrame
}

const frames: Ref<Registered[]> = ref([])
const route = ref('')
let nextId = 1

export function setScreenRoute(path: string): void {
    route.value = path
}

export function registerFrame(frame: ScreenFrame): { update: (f: ScreenFrame) => void; remove: () => void } {
    const id = nextId++
    frames.value = [...frames.value, { id, frame }]
    return {
        update(f) {
            frames.value = frames.value.map((r) => (r.id === id ? { id, frame: f } : r))
        },
        remove() {
            frames.value = frames.value.filter((r) => r.id !== id)
        },
    }
}

/** Publish a frame for the lifetime of the calling component. */
export function useScreenFrame(getter: () => ScreenFrame | null | undefined): void {
    let handle: ReturnType<typeof registerFrame> | null = null
    const stop = watch(
        getter,
        (f) => {
            if (!f) {
                handle?.remove()
                handle = null
                return
            }
            if (handle) handle.update(f)
            else handle = registerFrame(f)
        },
        { immediate: true, deep: true },
    )
    if (getCurrentInstance()) {
        onBeforeUnmount(() => {
            stop()
            handle?.remove()
            handle = null
        })
    }
}

function dedupe(entities: ScreenEntity[]): ScreenEntity[] {
    const seen = new Map<string, ScreenEntity>()
    for (const e of entities) {
        const key = `${e.type}:${e.id}`
        const prev = seen.get(key)
        seen.set(key, prev ? { ...prev, ...e, primary: Boolean(prev.primary || e.primary) } : e)
    }
    return [...seen.values()]
}

/** Compose the live frames (oldest → newest) into one ScreenContext. */
export function composeScreen(list: ScreenFrame[], path = ''): ScreenContext {
    const out: ScreenContext = { v: 1, route: path, page: '', entities: [] }
    let entities: ScreenEntity[] = []
    let primarySet = false
    for (const f of list) {
        if (f.page) out.page = f.page
        if (f.entities?.length) {
            // The newest frame that marks a primary entity owns "primary".
            if (f.entities.some((e) => e.primary)) {
                entities = entities.map((e) => ({ ...e, primary: false }))
                primarySet = true
            }
            entities = [...entities, ...f.entities]
        }
        if (f.selection !== undefined) out.selection = f.selection && f.selection.ids.length ? f.selection : undefined
        if (f.view !== undefined) out.view = f.view ?? undefined
        if (f.composer !== undefined) out.composer = f.composer ?? undefined
        if (f.visible !== undefined) out.visible = f.visible ?? undefined
        if (f.unsaved?.length) out.unsaved = [...(out.unsaved ?? []), ...f.unsaved]
    }
    entities = dedupe(entities)
    if (!primarySet && entities.length) entities[entities.length - 1] = { ...entities[entities.length - 1], primary: true }
    out.entities = entities.slice(-LIMITS.entities)
    if (out.selection) {
        const total = out.selection.total ?? out.selection.ids.length
        out.selection = { ...out.selection, ids: out.selection.ids.slice(0, LIMITS.selectionIds), total }
    }
    if (out.visible) out.visible = { ...out.visible, ids: out.visible.ids.slice(0, LIMITS.visibleIds) }
    if (out.composer?.body && out.composer.body.length > LIMITS.composerBody) {
        out.composer = { ...out.composer, body: out.composer.body.slice(0, LIMITS.composerBody) }
    }
    if (out.unsaved) out.unsaved = out.unsaved.slice(0, LIMITS.unsaved)
    return out
}

export function snapshotScreen(): ScreenContext {
    return composeScreen(
        frames.value.map((r) => r.frame),
        route.value || (typeof window !== 'undefined' ? window.location.pathname : ''),
    )
}

export function useScreenContext() {
    return { frames, snapshot: snapshotScreen }
}

/** Tests only. */
export function _resetScreen(): void {
    frames.value = []
    route.value = ''
}
